import type {
  CtaBannerContent,
  FaqContent,
  FeatureBlockContent,
  Stat,
  TestimonialGridContent,
  TextSectionContent,
} from './types';

/* Shared default content used across all product pages (per-page overrides allowed). */

export const defaultStats: Stat[] = [
  { icon: 'star', value: '4.4 ★', label: 'On the App Store' },
  { icon: 'users', value: '500k+', label: 'nsave users' },
  { icon: 'globe', value: '150+', label: 'Supported countries' },
  { icon: 'bank', value: '$1B+', label: 'Yearly money moved' },
  { icon: 'shuffle', value: '1m+', label: 'Transactions a year' },
];

export const trustedBySection: TextSectionContent = {
  title: 'Trusted by 500k+ users\nacross the globe',
  countUpHeading: true,
  body: 'nsave opens up the global banking system to people traditional banks leave behind. Wherever you are, you can hold USD, GBP and EUR, get real account details in your own name, and move money across borders the way people in stable economies always have.',
  link: { label: 'How to get started', href: '/help' },
  publications: {
    src: '/images/news/publications.png',
    webpSrc: '/images/news/publications-400.webp 400w',
    sizes: '(max-width: 440px) calc(100vw - 40px), 400px',
    width: 804,
    height: 62,
    alt: 'Featured in Financial Times, TechCrunch, Fintech Finance News and Bloomberg',
  },
};

export const investorsSection = {
  title: "Backed by the same investors as the world's best",
  logos: [
    { name: 'Y Combinator', src: '/images/investors/yc.png' },
    { name: 'Sequoia', src: '/images/investors/sequoia.png' },
    { name: 'TQ Ventures', src: '/images/investors/tq.png' },
    { name: 'Pioneer Fund', src: '/images/investors/pioneer.png' },
    { name: 'Ace & Company', src: '/images/investors/ace.png' },
    { name: 'Humba Ventures', src: '/images/investors/humba.png' },
    { name: 'Basis Set', src: '/images/investors/basis-set.png' },
  ],
};

export const defaultTestimonials: TestimonialGridContent = {
  title: 'Built for people just like you',
  items: [
    {
      label: 'Remote Workers',
      image: {
        src: '/images/shared/testimonial-remote-workers.jpg',
        alt: 'Remote worker using nsave',
      },
    },
    {
      label: 'Freelancers',
      image: { src: '/images/shared/testimonial-freelancers.jpg', alt: 'Freelancer using nsave' },
    },
    {
      label: 'Travelers',
      image: { src: '/images/shared/testimonial-travelers.jpg', alt: 'Traveler using nsave' },
    },
  ],
};

export const defaultFaq: FaqContent = {
  title: 'Frequently asked Questions',
  items: [
    {
      question: 'Who can open a USD, GBP or EUR account with nsave?',
      answer:
        'nsave is built for people in supported countries who want access to stable currencies. Download the app, verify your identity and open your account in minutes.',
    },
    {
      question: 'Are these real bank account details?',
      answer:
        'Yes. You get real account details in your own name — ACH and wire details for USD, sort code and account number for GBP, and an IBAN for EUR.',
    },
    {
      question: 'How safe is my money?',
      answer:
        'Your funds are held in segregated accounts, kept fully separate from our own, so they are always yours and never used to run the business.',
    },
    {
      question: 'How long does it take to open an account?',
      answer:
        'Most customers are verified and ready to go within minutes. In some cases additional checks can take up to one business day.',
    },
    {
      question: 'What does it cost?',
      answer:
        'Opening an account is free. You pay one transparent fee on conversions and transfers — the real exchange rate with no hidden spread.',
    },
    {
      question: 'Which currencies can I hold and convert between?',
      answer:
        'You can hold and convert between USD, GBP and EUR, and send money home to local currencies like PKR, BDT, EGP and more.',
    },
    {
      question: 'How do I add money or get paid?',
      answer:
        'Share your account details with employers, clients or platforms to receive transfers, or deposit via stablecoin and local rails in supported countries.',
    },
  ],
};

export const defaultCtaBanner: CtaBannerContent = {
  title: '4.4 of 5.0 on the App Store and Google Play Store',
};

/** Re-export store URLs so content modules can share the canonical links. */
export { APP_STORE_URL, GET_THE_APP_PATH, GET_THE_APP_URL, PLAY_STORE_URL } from '~/lib/appStores';

/* Shared "Download the nsave app" FeatureBlock, reused across product pages. */
export const appDownloadSection: FeatureBlockContent = {
  title: 'Download the nsave app',
  image: { src: '/images/shared/profile.jpg', alt: 'Person using the nsave app' },
  rows: [
    {
      icon: 'bank',
      title: 'Your global $, €, £ Account',
      description:
        'Check balances, move money and manage your accounts from anywhere, right on your phone.',
    },
    {
      icon: 'zap',
      title: 'Get started in ~5min',
      description:
        'Open your account and get started in just a few taps. No paperwork, no branches.',
    },
    {
      icon: 'shield',
      title: 'Safe and secure',
      description:
        'Your secure account. Instant card freeze and real-time alerts keep your money protected.',
    },
  ],
};

export const safeSpaceSection: TextSectionContent = {
  title: 'The safe space for\nyour money',
  body: "Your money's safety comes first at nsave. We hold your funds in segregated accounts, kept fully separate from our own, so they're always yours and never used to run the business. A dedicated engineering team protects your balance around the clock, and a real support team is on hand whenever you hit a problem, so help is always a message away.",
  link: { label: 'About nsave', href: '/about' },
  bordered: true,
};
